import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * Which product (project) the reader is in.
 *
 * The header needs it on every page, and most pages know it only after they
 * have loaded: a run knows its bench, a bench knows its project. So a page
 * SAYS which product it belongs to (`useSetProduct`), the address says it
 * directly for /projects/:id/..., and the last one is remembered so that
 * pages belonging to no product (Runs, Reports, People) keep the switcher on
 * what the reader was last looking at rather than blanking it.
 */
type Ctx = { id: number | null; set: (id: number | null) => void };
const ProductContext = createContext<Ctx>({ id: null, set: () => {} });
const KEY = "hilc.product";

function remembered(): number | null {
  try {
    const v = Number(window.localStorage.getItem(KEY));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

export function ProductProvider({ children }: { children: React.ReactNode }) {
  const [id, setId] = useState<number | null>(remembered);
  const { pathname, search } = useLocation();

  // The address wins where it names the product.
  useEffect(() => {
    const m = /^\/projects\/(\d+)/.exec(pathname);
    const q = new URLSearchParams(search).get("project");
    const named = m ? Number(m[1]) : q && /^\d+$/.test(q) ? Number(q) : null;
    if (named) setId(named);
  }, [pathname, search]);

  useEffect(() => {
    if (id === null) return;
    try {
      window.localStorage.setItem(KEY, String(id));
    } catch {
      /* private window: the switcher simply forgets on reload */
    }
  }, [id]);

  const value = useMemo(() => ({ id, set: setId }), [id]);
  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
}

export function useProduct(): Ctx {
  return useContext(ProductContext);
}

/** A page that knows its product says so once it has loaded. */
export function useSetProduct(id: number | null | undefined): void {
  const { set } = useProduct();
  useEffect(() => {
    if (id) set(id);
  }, [id, set]);
}

/**
 * Two characters that stand for a product on its tile.
 *
 * DisplayOS -> DO (its capitals), SNCF AO2025 -> SN (a word in capitals keeps
 * its first two), IPI-6 -> I6 and io-core -> io (hyphenated: the first
 * character of each part, lower case kept). Anything else: its first two.
 */
export function monogram(name: string): string {
  const n = name.trim();
  if (!n) return "?";
  const first = n.split(/\s+/)[0];
  if (first.includes("-")) {
    const parts = first.split("-").filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).slice(0, 2);
  }
  if (/^[A-Z0-9]+$/.test(first)) return first.slice(0, 2);
  const caps = first.replace(/[^A-Z]/g, "");
  if (caps.length >= 2) return caps.slice(0, 2);
  return first.slice(0, 2);
}

/** A product's own colour, for its tile only. None of them is the red,
 *  yellow or green that mean a state, so a tile never reads as a verdict.
 *  Stable: by name for the known products, else by id, so a product keeps
 *  its colour as others are added. */
const HUES = ["#2a6fb0", "#6a4fb3", "#0f7f86", "#8c5a2b", "#3d5a80", "#9a4d8f"];
/** The four products keep the colours the approved demo gave them, by name:
 *  by id, DisplayOS came out brown and io-core blue - the demo's colours
 *  swapped, on the product people look at most. */
const NAMED: Record<string, string> = {
  displayos: "#2a6fb0",
  sncf: "#6a4fb3",
  ipi6: "#0f7f86",
  iocore: "#8c5a2b",
};
export function productHue(id: number, name = ""): string {
  const key = name.toLowerCase().replace(/[^a-z0-9]/g, "");
  const named = Object.keys(NAMED).find((k) => key === k || key.startsWith(k));
  if (named) return NAMED[named];
  return HUES[(Math.abs(id) - 1 + HUES.length * 10) % HUES.length];
}
